'use client'
import {} from 'react'
import Section from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import styles from './MemberStoriesCarousel.module.scss'
import { LinkButton, Typography, MediaImageStandard } from 'components/Contentful'
import { AchieveTheme, Grid, Paper } from '@achieve/ascend'
import { Carousel } from 'components/Carousels'
import { Eyebrow } from 'components/Eyebrow'
import classNames from 'classnames'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import { BLOCKS } from '@contentful/rich-text-types'

function Card({ item: { cardImage, cardImageResponsive, cardText, cardTitleText } }) {
  const CardContent = (
    <Grid
      container
      direction={{ xs: 'column', lg: 'row' }}
      justifyContent={{ xs: 'flex-start', lg: 'flex-start' }}
      alignItems={{ xs: 'stretch', lg: 'center' }}
      spacing={{ xs: 4 }}
    >
      <Grid size={{ xs: 12, lg: 12 }}>
        <div className={styles['block-img']}>
          {cardImageResponsive && (
            <div className="absolute inset-0 z-0 block lg:hidden">
              <MediaImageStandard
                layout="fill"
                objectFit="cover"
                objectPosition="top right"
                preload={false}
                content={cardImageResponsive}
              />
            </div>
          )}
          <div className="absolute inset-0 z-0 hidden lg:block">
            <MediaImageStandard
              layout="fill"
              objectFit="cover"
              objectPosition="top right"
              preload={false}
              content={cardImage}
            />
          </div>
          <Grid
            container
            direction="column"
            justifyContent={{ xxs: 'flex-end', lg: 'center' }}
            className={styles['content']}
            data-testid="member-story-item-container"
          >
            <Typography
              content={cardTitleText}
              color={AchieveTheme?.ascend?.colors?.neutral?.[0]}
              data-testid="member-story-item-title"
              className={'headingMd lg:displayXl font-bold!'}
            />
            <Typography
              className={classNames(styles['sub-title'], 'bodySm lg:bodyLg font-light!')}
              content={cardText}
              color={AchieveTheme?.ascend?.colors?.neutral?.[0]}
              data-testid="member-story-item-subtitle"
            />
          </Grid>
        </div>
      </Grid>
    </Grid>
  )

  return (
    <Paper elevation={0} className={styles['paper']}>
      <>{CardContent}</>
    </Paper>
  )
}
function CardSm({ item: { cardImage, cardText, cardTitleText } }) {
  const image = cardImage
  const CardContent = (
    <Grid
      container
      direction={{ xs: 'column' }}
      justifyContent={{ xs: 'flex-start' }}
      alignItems={{ xs: 'stretch' }}
      spacing={{ xs: 4 }}
      className="w-[331px]"
    >
      <Grid size={{ xs: 12, lg: 12 }} className="max-w-full ">
        <div className="relative">
          <MediaImageStandard
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            height={416}
            width={301}
            preload={false}
            quality={100}
            content={image}
          />
          <Grid
            container
            direction="column"
            justifyContent={{ xxs: 'flex-end' }}
            className="absolute inset-0 bg-black/50 p-8"
          >
            <Typography
              content={cardTitleText}
              color={AchieveTheme?.ascend?.colors?.neutral?.[0]}
              className="pb-1"
              fontWeight="bold"
              variant="headingMd"
              data-testid="member-story-item-title"
            />
            <Typography
              className="w-max space-y-2 leading-relaxed [&_svg]:fill-white"
              content={cardText}
              color={AchieveTheme?.ascend?.colors?.neutral?.[0]}
              fontWeight="regular"
              variant="labelMd"
              data-testid="member-story-item-subtitle"
            />
          </Grid>
        </div>
      </Grid>
    </Grid>
  )

  return CardContent
}

function MemberStoriesCarousel({ content }) {
  const memoizedContent = useMemoizedContentGetter(content, [
    'title',
    'subTitle',
    'eyebrow',
    'MemberStoriesCarousel',
    'theme',
    'disclaimer',
    'config',
    'linkButton',
  ])

  const {
    title,
    subTitle,
    eyebrow,
    theme,
    disclaimer,
    config,
    linkButton,
    MemberStoriesCarousel: { carouselItemsCollection },
  } = memoizedContent

  const variation = config?.jsonContent?.variation || 'xl'
  const variationDot = config?.jsonContent?.variationDot
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  const backgroundColor =
    theme?.theme[0] === 'light-blue'
      ? AchieveTheme?.ascend?.colors?.blue?.[50]
      : AchieveTheme?.ascend?.colors?.neutral?.[0]

  return (
    <Section
      contain={false}
      className={styles['section']}
      backgroundColor={backgroundColor}
      data-testid="member-stories-carousel-section"
    >
      {variation == 'sm' ? (
        <div className="w-full">
          {eyebrow && (
            <Typography
              content={eyebrow?.textContent}
              variant="labelSm"
              color="gray"
              fontWeight="bold"
              className="uppercase pb-5 pt-12 text-center tracking-eyebrow"
            />
          )}
          {title && (
            <Typography
              content={title?.textContent}
              variant="headingLg"
              mobileVariant="headingMd"
              responsive={true}
              className="pb-5 text-center"
            />
          )}
          {subTitle && (
            <Typography
              content={subTitle?.textContent}
              variant="displayXs"
              fontWeight="regular"
              className="pb-5 text-center"
            />
          )}
          {linkButton && (
            <div
              className={classNames(
                'hidden lg:flex flex-col justify-center items-center w-full pb-12'
              )}
            >
              <LinkButton
                // TODO move to tailwind config for FDR
                className={classNames('min-w-[105px] h-[47px]', {
                  'bg-[#cb000e]': siteEnv === SITE_ENVIRONMENTS.FDR,
                })}
                content={linkButton}
                typographicOptions={{
                  variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
                  fontWeight: 'bold',
                  'data-testid': `member-stories-carousel-button-text`,
                  className: 'min-w-[105px] text-center',
                }}
                color={'primary'}
                data-testid={`member-stories-carousel-button`}
                track={{
                  ...{
                    list_name: 'Personalized member-stories-carousel CTA',
                    click_id: linkButton.linkText?.json.content?.[0]?.content[0]?.value,
                    click_text: linkButton.linkText?.json.content[0]?.content[0]?.value,
                    click_url: linkButton.linkHref,
                    nav_link_section: 'member-stories-carousel',
                    click_position: '0',
                    track_event: 'internal_campaign_click',
                    event_action: 'button_click',
                    event_type: 'button_click',
                  },
                  ...linkButton.event,
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <Eyebrow
          data-testid="featured-story-eyebrow"
          content={title?.textContent}
          variant="bodySm"
          fontWeight="bold"
          component="h3"
        />
      )}
      <Carousel
        carouselItems={carouselItemsCollection?.items}
        itemComponent={variation === 'sm' ? CardSm : Card}
        disableArrows={variation === 'sm'}
        variant={variation === 'sm' ? 'preview-small' : 'preview'}
        variationDot={variationDot}
        idPrefix="member-stories-carousel"
        withIndicators={true}
        classNameIndicators={classNames(
          styles['hero-carousel-wrapper-indicators'],
          'flex lg:hidden'
        )}
        classNameControls={styles['hero-carousel-wrapper-controls']}
        classNameActiveDot={styles['carousel-active']}
        classNameContainer={
          variation === 'sm' && 'xxl:mx-[152px] xl:mx-[140px] gap-2 sm:gap-0 md:gap-14 lg:gap-0'
        }
        classNameIndicatorDots={
          variation === 'sm' && 'flex bg-[#3300ff] mt-6 lg:hidden hover:cursor-pointer'
        }
        classNameItems="mr-0 xs:mr-0"
      />
      {disclaimer && (
        <div className=" flex items-center justify-center">
          <div className="text-gray-500 pb-10 pt-6">
            <Typography
              content={disclaimer?.textContent}
              className=" text-center"
              variant="bodyXs"
            />
          </div>
        </div>
      )}
    </Section>
  )
}
export { MemberStoriesCarousel }
export default MemberStoriesCarousel
